import React from 'react'

import { Link, graphql, useStaticQuery } from 'gatsby'

import { CustomerContext } from '@context/customerContext'

import { MegaMenuType } from '@contracts/wp/megaMenu'

import * as styles from './HeaderMenu.module.scss'
import NormalLinks from './NormalLinks'
import ProductsDropdown from './ProductsDropdown'
import SectorsDropdown from './SectorsDropdown'

import LoginIcon from '@icons/login.svg'
import PhoneIcon from '@icons/sales.svg'

interface Props {
	isMenuOpen: boolean
	className?: string
}

type MenuDataType = {
	wp: {
		themeSettings: {
			themeSettings: {
				salesNumber: string
			}
		}
		acfOptionsHeaderSettings: {
			headerSettings: {
				megaMenu: MegaMenuType
			}
		}
	}
}

const HeaderMenu: React.FC<Props> = ({ isMenuOpen, className }) => {
	const menuData = useStaticQuery<MenuDataType>(graphql`
		query HeaderMenuData {
			wp {
				themeSettings {
					themeSettings {
						salesNumber
					}
				}
				acfOptionsHeaderSettings {
					headerSettings {
						megaMenu {
							normalLinks {
								megaMenuItem {
									label
									link {
										... on WpPage {
											title
											uri
											slug
										}
										... on WpPost {
											title
											uri
											slug
										}
										... on WpSector {
											title
											uri
											slug
										}
									}
								}
								submenu {
									megaMenuItem {
										label
										link {
											... on WpPage {
												title
												uri
												slug
											}
											... on WpPost {
												title
												uri
												slug
											}
											... on WpSector {
												title
												uri
												slug
											}
										}
									}
								}
							}
							sectorsDropdown {
								sectorsLinks {
									megaMenuItem {
										label
										link {
											... on WpSector {
												title
												uri
											}
										}
									}
								}
								sectorsCards {
									megaMenuCard {
										image {
											...wpMediaItem
											localFile {
												childImageSharp {
													gatsbyImageData(width: 350, aspectRatio: 1)
												}
											}
										}
										megaMenuItem {
											label
											link {
												... on WpPage {
													title
													uri
													slug
												}
												... on WpPost {
													title
													uri
													slug
												}
												... on WpSector {
													title
													uri
													slug
												}
											}
										}
									}
								}
							}
							productsDropdown {
								productCategories {
									activeCategories {
										category {
											name
											uri
										}
										categoryProducts {
											product {
												... on WpProduct {
													title
													uri
												}
											}
										}
									}
									otherCategories {
										category {
											name
											uri
										}
									}
								}
								productCards {
									megaMenuCard {
										image {
											...wpMediaItem
											localFile {
												childImageSharp {
													gatsbyImageData(width: 350, aspectRatio: 1)
												}
											}
										}
										megaMenuItem {
											label
											link {
												... on WpPage {
													title
													uri
													slug
												}
												... on WpPost {
													title
													uri
													slug
												}
												... on WpSector {
													title
													uri
													slug
												}
											}
										}
									}
								}
							}
						}
					}
				}
			}
		}
	`)

	const [user] = React.useContext(CustomerContext)

	const [headerHeight, setHeaderHeight] = React.useState(62)

	const { megaMenu } = menuData.wp.acfOptionsHeaderSettings.headerSettings
	const { salesNumber } = menuData.wp.themeSettings.themeSettings

	const menuStyles = [styles.menu]

	if (isMenuOpen) {
		menuStyles.push(styles.open)
	}

	React.useEffect(() => {
		const bodyTag = document.getElementsByTagName('body')[0]

		if (bodyTag) {
			bodyTag.setAttribute(
				'style',
				`height: ${isMenuOpen ? '100svh' : 'auto'}; overflow: ${isMenuOpen ? 'hidden' : 'unset'}`
			)
		}
	}, [isMenuOpen])

	React.useEffect(() => {
		const header = document.getElementById('header')

		if (header) {
			const headerHeight = header?.getBoundingClientRect().height

			if (headerHeight) {
				setHeaderHeight(headerHeight)
			} else {
				setHeaderHeight(62)
			}
		}
	}, [isMenuOpen])

	React.useEffect(() => {
		const updateHeaderHeight = () => {
			const header = document.getElementById('header')

			if (header) {
				header.setAttribute('style', `--header-height: ${headerHeight}px`)
			}
		}

		updateHeaderHeight()

		window.addEventListener('resize', updateHeaderHeight)

		return window.removeEventListener('resize', updateHeaderHeight)
	}, [headerHeight])

	return (
		<nav className={menuStyles.join(' ')}>
			{megaMenu.productsDropdown ? <ProductsDropdown data={megaMenu.productsDropdown} /> : null}

			{megaMenu.sectorsDropdown ? <SectorsDropdown data={megaMenu.sectorsDropdown} /> : null}

			{megaMenu.normalLinks ? <NormalLinks data={megaMenu.normalLinks} /> : null}

			<ul className={styles.accountNav}>
				<li className={[styles.accountNavItem, styles.login].join(' ')}>
					<LoginIcon className={styles.icon} />

					<Link to="/login" className={styles.link}>
						{user ? user.firstName : 'Login'}
					</Link>
				</li>

				<li className={[styles.accountNavItem, styles.sales].join(' ')}>
					<PhoneIcon className={styles.icon} />

					<div className={styles.link}>
						<strong>Sales: </strong>

						<a href={`tel:${salesNumber.replace(' ', '')}`}>{salesNumber}</a>
					</div>
				</li>
			</ul>
		</nav>
	)
}

export default HeaderMenu
